/**
 * ADR-70 cut 1: the ring console never acts on a screen the ring has moved
 * past, and says so in one line when it was refused for that.
 *
 * Pure, so the rules are testable without a browser or a ring:
 *
 *  - expectedView: the part of the screen a lot command was decided on, sent
 *    back as `expected` so the ring can refuse it (STALE_AUCTION_STATE) when
 *    the lot, the standing bid or the held internet bid has changed since.
 *  - answerLine: what the clerk is told about a floor command's answer.
 *    Accepted says nothing (the board moving is the answer); a refusal says
 *    why, briefly, without a dialog.
 *  - highBidFromSnapshot: who holds the bid, read back from each floor
 *    snapshot, so a reconnect or a stale refusal cannot leave the console
 *    believing an older high bid.
 */

import { nextAskByStep } from '@hipnumber/auction-core';
import type { FloorCommandResult } from '@hipnumber/protocol-client';

/** What the ring compares a guarded command against (auction-core ExpectedRingView). */
export interface ExpectedRingView {
  lotId: string | null;
  highBidSeq: number | null;
  pendingBidId?: string | null;
  ask?: number | null;
}

/** The snapshot fields the view is read from. */
export interface RingViewSource {
  lotId: string | null;
  currentAsk: number | null;
  [extra: string]: unknown;
}

/**
 * The view to send with a guarded command, or undefined when the ring is older
 * than the field (no `highBidSeq` on its snapshot) and would not check it.
 *
 * The held internet bid is part of the view whenever the snapshot carries
 * one: hammering, passing or moving the ask over an internet bid the clerk
 * has not seen is exactly the mistake the guard is for. `ask` is added for a
 * command computed from the ask (SET_ASK).
 */
export function expectedView(snap: RingViewSource | null, opts: { ask?: boolean } = {}): ExpectedRingView | undefined {
  if (!snap) return undefined;
  const seq = snap['highBidSeq'];
  if (seq !== null && typeof seq !== 'number') return undefined;
  const view: ExpectedRingView = { lotId: snap.lotId, highBidSeq: seq };
  if ('pendingBid' in snap) {
    const p = snap['pendingBid'] as { pendingBidId?: unknown } | null | undefined;
    view.pendingBidId = typeof p?.pendingBidId === 'string' ? p.pendingBidId : null;
  }
  if (opts.ask) view.ask = snap.currentAsk;
  return view;
}

/** The command with the screen's view attached (unchanged on an older ring). */
export function withExpected<C extends Record<string, unknown>>(command: C, snap: RingViewSource | null, opts: { ask?: boolean } = {}): C & { expected?: ExpectedRingView } {
  const expected = expectedView(snap, opts);
  return expected ? { ...command, expected } : command;
}

/** Shown while the screen cannot be trusted (ProtocolClient.ringLive false). */
export const DEGRADED_LINE = 'Reconnecting - sell and pass are off until the ring answers';
/** Shown when the ring refused a press made on an older screen. */
export const STALE_LINE = 'The ring moved on - screen refreshed, check and press again';
/** ADR-70 cut 3: shown when the ring refused to leave a lot holding a bid. */
export const LOT_IN_PROGRESS_LINE = 'This lot still holds a bid - sell or pass it before moving on';
/** ADR-70 cut 6d: shown when the ring refused to take a lot holding a bid out of the ring. */
export const REMOVE_HOLDS_BID_LINE = 'Not taken out - the lot holds a bid. Remove the bids or reset bidding first';
/** ADR-70 cut 6e: shown when the ring refused a look at the max bids. */
export const MAX_BIDS_OFF_LINE = 'Max bids are not shown here - a Super Admin has not turned it on for this company';

export type CloseStepName = 'GOING_ONCE' | 'GOING_TWICE' | 'LAST_CALL';
const NEXT_STEP: Record<CloseStepName, CloseStepName | null> = { GOING_ONCE: 'GOING_TWICE', GOING_TWICE: 'LAST_CALL', LAST_CALL: null };
const STEP_WORDS: Record<CloseStepName, string> = { GOING_ONCE: 'Going once', GOING_TWICE: 'Going twice', LAST_CALL: 'Last call' };
const asStep = (v: unknown): CloseStepName | null => (v === 'GOING_ONCE' || v === 'GOING_TWICE' || v === 'LAST_CALL' ? v : null);

/** The words for the step a closing lot stands at, or null (a plain fair
 *  warning on an older ring reads "Going once"). */
export function closeStepWords(snap: { lotState: string; [extra: string]: unknown } | null): string | null {
  if (snap?.lotState !== 'FAIR_WARNING') return null;
  const s = asStep(snap['closeStep']);
  return s ? STEP_WORDS[s] : STEP_WORDS.GOING_ONCE;
}

/**
 * ADR-70 cut 3: what the close button does next, or null when there is no
 * next step (not bidding, or already at last call - the hammer is next).
 *
 * A ring that reports `ringPhase` takes SET_CLOSE_STEP, so the one button
 * walks going once, going twice, last call. An older ring knows only the
 * fair warning, once, and the button falls back to it. A plain fair warning
 * (no step) reads as going once, so the next step is going twice.
 */
export function closeStepAction(snap: (RingViewSource & { lotState: string }) | null): { command: Record<string, unknown>; label: string } | null {
  if (!snap?.lotId) return null;
  const steps = typeof snap['ringPhase'] === 'string';
  if (snap.lotState === 'OPEN') {
    return steps
      ? { command: { type: 'SET_CLOSE_STEP', lotId: snap.lotId, step: 'GOING_ONCE' }, label: STEP_WORDS.GOING_ONCE }
      : { command: { type: 'DECLARE_FAIR_WARNING', lotId: snap.lotId }, label: STEP_WORDS.GOING_ONCE };
  }
  if (snap.lotState !== 'FAIR_WARNING' || !steps) return null;
  const next = NEXT_STEP[asStep(snap['closeStep']) ?? 'GOING_ONCE'];
  return next ? { command: { type: 'SET_CLOSE_STEP', lotId: snap.lotId, step: next }, label: STEP_WORDS[next] } : null;
}

export interface AnswerLine {
  text: string;
  /** A toast as well as the activity feed. */
  toast: boolean;
}

/**
 * What to tell the clerk about a floor command's answer; null for nothing.
 *
 * `auto` marks a command the console sent by itself (advance after a result,
 * open on arrival): a refusal of one goes to the feed only, since "press
 * again" would describe a press nobody made.
 */
export function answerLine(r: FloorCommandResult, origin: 'press' | 'auto' = 'press'): AnswerLine | null {
  const toast = origin === 'press';
  switch (r.status) {
    case 'ACCEPTED':
    case 'SENT':
      return null;
    case 'NOT_SENT':
      if (r.reason === 'RESYNCING') return { text: `${r.commandType} not sent - the screen is catching up with the ring`, toast };
      return { text: `${r.commandType} not sent - the ring is reconnecting`, toast };
    case 'TIMEOUT':
      if (r.reason === 'CLIENT_CLOSED') return null;
      // The outcome is unknown, not refused: the board says whether it landed.
      return { text: `No answer to ${r.commandType} - check the board before pressing again`, toast: true };
    case 'REJECTED':
      if (r.reason === 'STALE_AUCTION_STATE') return { text: origin === 'auto' ? `${r.commandType} skipped - the ring had moved on` : STALE_LINE, toast };
      if (r.reason === 'INTERNAL_RETRY') return { text: `The ring could not take ${r.commandType} just now - press again`, toast };
      // ADR-70 cut 3 (Q4): the ring will not walk away from a bid.
      if (r.reason === 'LOT_IN_PROGRESS' && r.commandType !== 'REMOVE_FROM_RING') return { text: origin === 'auto' ? `${r.commandType} skipped - the lot still holds a bid` : LOT_IN_PROGRESS_LINE, toast };
      // ADR-70 cut 6c: another bid landed, or the hammer fell, in between.
      if (r.reason === 'CORRECTION_REQUIRES_RECONCILIATION') return { text: 'Not removed - that bid is no longer the last one, or the lot stopped taking bids', toast };
      // ADR-70 cut 6d: the increment and taking a lot out of the ring.
      if (r.commandType === 'REMOVE_FROM_RING') {
        if (r.reason === 'LOT_IN_PROGRESS') return { text: REMOVE_HOLDS_BID_LINE, toast };
        if (r.reason === 'ILLEGAL_TRANSITION') return { text: 'Not taken out - the lot already has a result', toast };
      }
      if (r.commandType === 'SET_INCREMENT') {
        if (r.reason === 'INVALID_INCREMENT') return { text: 'Not changed - the increment must be a whole dollar amount of $1 or more', toast };
        if (r.reason === 'ILLEGAL_TRANSITION') return { text: 'That is already the increment', toast };
        if (r.reason === 'LOT_NOT_OPEN') return { text: 'Not changed - the increment changes only while the lot is up', toast };
      }
      // ADR-70 cut 6e: the Super Admin's switch is off (or went off after the
      // screen offered Show).
      if (r.reason === 'CAPABILITY_OFF') return { text: MAX_BIDS_OFF_LINE, toast };
      return { text: r.reason ?? `${r.commandType} rejected`, toast };
  }
}

export interface HighBidView {
  highIsFloor: boolean;
  highPaddle: string | null;
  lastFloorBidSeq: number | null;
}

/**
 * The standing bid as the floor snapshot states it.
 *
 * The undo target is only ever narrowed here: a correction leaves nothing
 * to correct (the ring keeps no second undo record), which the snapshot
 * cannot show, so a floor bid this console did not see land is never
 * offered for undo. It survives only while it is still the standing bid.
 */
export function highBidFromSnapshot(snap: RingViewSource & { currentHighBid: number | null; highBidderRef: string | null }, lastFloorBidSeq: number | null): HighBidView {
  if (snap.currentHighBid === null) return { highIsFloor: false, highPaddle: null, lastFloorBidSeq: null };
  const highIsFloor = snap.highBidderRef === 'Floor';
  const paddle = snap['highPaddle'];
  const seq = snap['highBidSeq'];
  return {
    highIsFloor,
    highPaddle: highIsFloor && typeof paddle === 'string' && paddle !== '' ? paddle : null,
    // An older ring (no highBidSeq) keeps the event-tracked value.
    lastFloorBidSeq: !highIsFloor ? null : typeof seq === 'number' ? (seq === lastFloorBidSeq ? lastFloorBidSeq : null) : lastFloorBidSeq,
  };
}

/** ADR-70 cut 2: the equal-bid policies a ring can run (auction-core EqualBidPolicy). */
export type EqualBidPolicyName = 'AUCTIONEER_AUTHORITY' | 'FIRST_SERVER_TIMESTAMP' | 'ONSITE_PRIORITY' | 'ONLINE_PRIORITY';

export interface EqualBidOffer {
  /** "Floor had it first": give the standing online bid's amount to the floor. */
  award: { bidSeq: number; amount: number } | null;
  /** Undo that award: the online bid stands again. */
  restore: { overrideSeq: number; amount: number } | null;
}

/**
 * Which equal-bid overrides the clerk may press, read from the floor snapshot.
 *
 * Offered only when the ring would take it: the policy allows it, the lot is
 * taking bids, and the standing bid is the one the button acts on (the award
 * names the online bid's seq; the restore needs the awarded floor bid still
 * standing). A ring older than the feature (no `equalBidPolicy` on its
 * snapshot) is offered neither.
 */
export function equalBidOffer(snap: (RingViewSource & { lotState?: string | null; currentHighBid: number | null; highBidderRef: string | null }) | null): EqualBidOffer {
  const none: EqualBidOffer = { award: null, restore: null };
  if (!snap) return none;
  const policy = snap['equalBidPolicy'];
  if (typeof policy !== 'string') return none;
  if (snap.lotState !== 'OPEN' && snap.lotState !== 'FAIR_WARNING') return none;
  const amount = snap.currentHighBid;
  const seq = snap['highBidSeq'];
  if (amount === null || typeof seq !== 'number') return none;
  const awardable = (policy === 'AUCTIONEER_AUTHORITY' || policy === 'ONSITE_PRIORITY') && snap.highBidderRef !== null && snap.highBidderRef !== 'Floor';
  const a = snap['equalBidAward'] as { overrideSeq?: unknown; floorBidSeq?: unknown } | null | undefined;
  const restorable = policy === 'AUCTIONEER_AUTHORITY' && a != null && typeof a.overrideSeq === 'number' && a.floorBidSeq === seq;
  return {
    award: awardable ? { bidSeq: seq, amount } : null,
    restore: restorable ? { overrideSeq: a.overrideSeq as number, amount } : null,
  };
}

/** ADR-70 cut 6c: the last bid on the lot, as the floor snapshot names it. */
export interface LastBidOffer { seq: number; amount: number; source: 'FLOOR' | 'ONLINE' | 'PROXY' }

export interface BidCorrectionOffer {
  /** "Remove last bid": the last bid, whatever its source. Null when there is
   *  none, or on a ring older than the field (see `legacy`). */
  retract: LastBidOffer | null;
  /** The ring predates RETRACT_LAST_BID (no `lastBid` on its snapshot): the
   *  console keeps the old floor-bid correction for it. */
  legacy: boolean;
  /** "Reset bidding": a lot taking bids with a bid on the board. */
  reset: boolean;
}

/**
 * ADR-70 cut 6c: what the clerk may take back or reset, read from the floor
 * snapshot so the buttons mirror the ring (which refuses the rest anyway).
 * A reset with only standing max bids and no bid on the board is legal on
 * the ring but not offered here: it is rare, and the board shows nothing to
 * reset.
 */
export function bidCorrectionOffer(snap: (RingViewSource & { lotState?: string | null; currentHighBid: number | null }) | null): BidCorrectionOffer {
  if (!snap) return { retract: null, legacy: false, reset: false };
  const bidding = snap.lotState === 'OPEN' || snap.lotState === 'FAIR_WARNING';
  const legacy = !('lastBid' in snap);
  const l = snap['lastBid'] as { seq?: unknown; amount?: unknown; source?: unknown } | null | undefined;
  const source = l?.source === 'FLOOR' || l?.source === 'ONLINE' || l?.source === 'PROXY' ? l.source : null;
  const retract: LastBidOffer | null = bidding && l != null && typeof l.seq === 'number' && typeof l.amount === 'number' && source !== null
    ? { seq: l.seq, amount: l.amount, source }
    : null;
  return { retract, legacy, reset: bidding && snap.currentHighBid !== null };
}

/** What a bid's source is called on the console's buttons. */
export const SOURCE_WORDS: Record<LastBidOffer['source'], string> = { FLOOR: 'floor', ONLINE: 'online', PROXY: 'max bid' };

/** ADR-70 cut 6d: the lot's increment, as the ring states it. */
export interface IncrementView {
  /** The step the ask buttons and the Increment line use. */
  step: number;
  /** The clerk chose it for this lot (it is not the sale's table). */
  chosen: boolean;
  /** The sale's table at the ask (floor snapshot), for the sheet's choices;
   *  null when the ring does not say. */
  table: number | null;
  /** The ring takes SET_INCREMENT and REMOVE_FROM_RING (its snapshot carries
   *  `increment`). An older ring is offered neither. */
  settable: boolean;
}

const posInt = (v: unknown): number | null => (typeof v === 'number' && Number.isSafeInteger(v) && v >= 1 ? v : null);

/**
 * The ring's own step (snapshot `increment`), so a sale on its own table
 * (regional-v1, a custom one) or a step the clerk chose shows the step the
 * ring will actually use. An older ring does not say, and `fallback` (the
 * default table at the ask, what the console showed before) stands in.
 */
export function incrementView(snap: (RingViewSource & { lotState?: string | null }) | null, fallback: number): IncrementView {
  const settable = !!snap && 'increment' in snap;
  const step = posInt(snap?.['increment']) ?? fallback;
  const table = posInt(snap?.['tableIncrement']);
  return { step, chosen: settable && snap?.['incrementChosen'] === true, table, settable };
}

const NICE = [1, 2, 2.5, 5];

/** The largest whole 1-2-2.5-5 number at or under `x` ($100, $250, $500...), or null under $1. */
export function niceStepAtOrBelow(x: number): number | null {
  if (!Number.isFinite(x) || x < 1) return null;
  let best = 1;
  for (let p = 1; p <= x; p *= 10) for (const n of NICE) { const v = n * p; if (v <= x && Number.isInteger(v) && v > best) best = v; }
  return best;
}

/**
 * The steps the increment sheet offers next to "back to the table": a
 * quarter, a half, double and five times the table's step, each rounded down
 * to a number an auctioneer says out loud. At a $1,000 table: $250, $500,
 * $2,000, $5,000. The table's own step and duplicates are left out.
 */
export function incrementChoices(table: number): number[] {
  const out: number[] = [];
  for (const f of [0.25, 0.5, 2, 5]) {
    const v = niceStepAtOrBelow(table * f);
    if (v !== null && v !== table && !out.includes(v)) out.push(v);
  }
  return out;
}

/**
 * The asking price a step would leave, by the ring's own rule: with a bid on
 * the board the ask becomes that bid plus the step (rounded to a multiple of
 * it); with none, the ask stands. Shown on each choice so the auctioneer sees
 * the number they will call before it is sent.
 */
export function askAfterStep(high: number | null, ask: number | null, step: number): number | null {
  return high !== null ? nextAskByStep(high, step) : ask;
}

/** Steps the clerk typed, kept on this device: the newest first, four at most. */
export const SAVED_STEPS_MAX = 4;
export function parseSavedSteps(raw: string | null): number[] {
  if (!raw) return [];
  try {
    const v: unknown = JSON.parse(raw);
    if (!Array.isArray(v)) return [];
    const out: number[] = [];
    for (const n of v) { const s = posInt(n); if (s !== null && !out.includes(s)) out.push(s); }
    return out.slice(0, SAVED_STEPS_MAX);
  } catch { return []; }
}
export function rememberStep(saved: readonly number[], step: number): number[] {
  return [step, ...saved.filter((s) => s !== step)].slice(0, SAVED_STEPS_MAX);
}
export function forgetStep(saved: readonly number[], step: number): number[] {
  return saved.filter((s) => s !== step);
}

/** A typed step: whole dollars, $1 or more ("$1,250" reads as 1250); null otherwise. */
export function parseStep(text: string): number | null {
  const t = text.replace(/[$,\s]/g, '');
  if (!/^\d+$/.test(t)) return null;
  return posInt(Number(t));
}

/**
 * ADR-70 cut 6d: "Take out of ring" - offered when the ring would take it:
 * a lot is up, it holds no bid, and it has no result. A lot holding a bid is
 * refused by the ring (LOT_IN_PROGRESS); the clerk removes the bids or
 * resets first. A ring older than the command is offered nothing.
 */
export function removeFromRingOffer(snap: (RingViewSource & { lotState?: string | null; currentHighBid: number | null }) | null): boolean {
  if (!snap?.lotId || !('increment' in snap)) return false;
  if (snap.lotState === 'UPCOMING') return true;
  return (snap.lotState === 'OPEN' || snap.lotState === 'FAIR_WARNING') && snap.currentHighBid === null;
}

/**
 * ADR-70 cut 5f: the words, and the way out, when the ring says the office
 * assigned this clerk to another ring of the sale. `rings` are the engine ring
 * ids the ring named; `sale` is the sale's rings (for their names). The ring
 * has closed this console for good, so the answer is a link, not a retry.
 */
export function notAssignedView(
  rings: readonly string[],
  here: { readonly engineRingId: string; readonly name: string },
  sale: readonly { readonly engineRingId: string; readonly name: string }[],
): { line: string; go: { ringId: string; name: string }[] } {
  const go = rings.filter((id) => id !== here.engineRingId)
    .map((id) => ({ ringId: id, name: sale.find((r) => r.engineRingId === id)?.name ?? id }));
  if (go.length === 0) return { line: `The office has not assigned you to ${here.name}. Ask the office which ring is yours.`, go };
  const names = go.map((r) => r.name);
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return { line: `The office assigned you to ${list}, not ${here.name}.`, go };
}
